import {
    ABSENTEE_BID_FAILURE,
    ABSENTEE_BID_REQUEST,
    ABSENTEE_BID_SUCCESS,
    DISMISS_MODAL,
    OPEN_ABSENTEE_BID_MODAL,
} from './actions';
import { getPlacedAbsenteeBidAnalytics, trackConversionForAlgolia } from './analytics';
import { ActionWithPayload } from '@/types/redux';
import { createSelector } from 'reselect';
import { fetchBidLimitBalanceIfNeeded } from './bidLimit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import { handleActions } from 'redux-actions';
import { isUiMobile } from './browser';
import { postPlaceAbsenteeBid } from '@/redux/api/bid';

// reducer
const DEFAULT_STATE = {
    bidAmount: null,
    bidPlaced: false,
    error: false,
    isCurrentHighBid: false,
    itemId: null,
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE & {
    bidAmount: number | null | undefined;
    itemId: number | null | undefined;
};

export const reducer = handleActions(
    {
        [ABSENTEE_BID_FAILURE]: (state: State) => ({
            ...state,
            bidPlaced: false,
            error: true,
            submitted: false,
            success: false,
        }),
        [ABSENTEE_BID_REQUEST]: (state: State) => ({
            ...state,
            bidPlaced: true,
            error: false,
            submitted: true,
            success: false,
        }),
        [ABSENTEE_BID_SUCCESS]: (state: State, action: ActionWithPayload<{ isCurrentHighBid: boolean }>) => ({
            ...state,
            bidAmount: null,
            error: false,
            isCurrentHighBid: action.payload.isCurrentHighBid,
            submitted: false,
            success: true,
        }),
        [DISMISS_MODAL]: (state: State) => ({
            ...state,
            bidPlaced: false,
        }),
        [OPEN_ABSENTEE_BID_MODAL]: (
            state: State,
            action: ActionWithPayload<{ bidAmount: number; itemId: number }>
        ) => ({
            ...state,
            bidAmount: action.payload.bidAmount,
            itemId: action.payload.itemId,
        }),
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.absenteeBid;

export const getUiAbsenteeBid = createSelector(stateSelector, (state) => state);

export const getAbsenteeBidAmount = createSelector(getUiAbsenteeBid, (uiState) => {
    return !isNaN(uiState.bidAmount) ? uiState.bidAmount : null;
});

export const getAbsenteeBidItemId = createSelector(getUiAbsenteeBid, (uiState) => {
    return uiState.itemId ? uiState.itemId : null;
});

/* ACTION CREATORS */
export const submitAbsenteeBid = (details: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const uiMobile = isUiMobile(state);
        const isMobile = uiMobile === 'tablet' || uiMobile === 'phone';

        details.client = 'React';
        details.deviceType = isMobile ? 'Mobile' : 'Desktop';

        dispatch({
            type: ABSENTEE_BID_REQUEST,
        });

        const response = await postPlaceAbsenteeBid({ authToken, deployment, details });
        const analytics = getPlacedAbsenteeBidAnalytics(state, details.itemId, details.bidAmount);
        dispatch(trackConversionForAlgolia(details.itemId));

        dispatch(fetchBidLimitBalanceIfNeeded(details.catalogId));

        dispatch({
            meta: {
                actionTime: Date.now(),
                analytics,
                itemId: details.itemId,
            },
            payload: response,
            type: ABSENTEE_BID_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: ABSENTEE_BID_FAILURE,
        });
    }
};
